import { Injectable } from '@angular/core';
import { BANK_APIS_FLAG, BANK_APIS_FLAG_DEBUG } from '@common/constants/bank-apis-flag';
import { readSupportSession } from '@modules/auth/models/support-session.model';
import { environment } from 'environments/environment';
import posthog from 'posthog-js';
import { Subject } from 'rxjs';

const FLAG_TIMEOUT_MS = 1500;
const BANK_APIS_LOG = '[FINA-3138 bank-apis]';

@Injectable({
    providedIn: 'root',
})
export class AnalyticsService {
    private initPromise: Promise<void> | null = null;
    private flagsLoadedCallbacks: Array<() => void> = [];
    private lastBankApisLogged: boolean | null = null;

    /** Emits whenever feature flags are loaded/updated. Subscribe to trigger re-renders. */
    flagsUpdated$ = new Subject<void>();

    /**
     * Initialize PostHog and wait for feature flags to load.
     * Called by APP_INITIALIZER before the app renders.
     *
     * PostHog caches flags in localStorage automatically:
     * - Return visits: flags available instantly from cache
     * - First visit: waits for flags from server (with timeout)
     */
    initialize(): Promise<void> {
        if (this.initPromise) {
            return this.initPromise;
        }

        this.initPromise = new Promise<void>(resolve => {
            let resolved = false;
            const done = () => {
                if (!resolved) {
                    resolved = true;
                    resolve();
                }
            };

            this.logBankApisFlag('init:start');

            posthog.init(environment.POSTHOG_KEY, {
                api_host: environment.POSTHOG_HOST,
                ui_host: 'https://us.posthog.com',
                autocapture: false,
                loaded: () => {
                    // Flags may be available from cache
                    this.logBankApisFlag('init:loaded', {
                        cachedFlagCount: posthog.featureFlags.getFlags().length,
                    });
                    if (posthog.featureFlags.getFlags().length > 0) {
                        done();
                    }
                },
            });

            // Single listener that notifies all waiting callbacks
            posthog.onFeatureFlags((...args: unknown[]) => {
                const flags = args[0];
                const variants = args[1];
                const extra = args[2] as { errorsLoading?: boolean } | undefined;
                this.logBankApisFlag('onFeatureFlags', {
                    callbackFlags: flags,
                    callbackVariants: variants,
                    errorsLoading: extra?.errorsLoading ?? null,
                });
                done();
                this.flagsLoadedCallbacks.forEach(cb => cb());
                this.flagsLoadedCallbacks = [];
                // Notify subscribers that flags have been updated
                this.flagsUpdated$.next();
            });

            setTimeout(() => {
                if (!resolved) {
                    this.logBankApisFlag('init:timeout-before-flags');
                }
                done();
            }, FLAG_TIMEOUT_MS);
        });

        return this.initPromise;
    }

    analyticsCapture(eventName: string, eventProperties: object = {}) {
        // Lo que hace soporte dentro del comercio no es uso del comercio.
        if (readSupportSession()) return;
        posthog.capture(eventName, eventProperties);
    }

    analyticsIdentify(userId: string, tenantId: string, userProperties: object = {}) {
        if (readSupportSession()) return;
        this.logBankApisFlag('identify:before', { userId, tenantId });
        posthog.identify(userId, userProperties);
        // Do not reload here. Login/autologin call `posthog.group('tenant', …)` and then
        // `reloadFeatureFlags()`. Reloading before the group evaluates `bank-apis` as
        // no_group_type (false); a later reload can lose the race and leave the kill-switch OFF.
        this.logBankApisFlag('identify:after', { userId, tenantId });
    }

    startSessionRecording() {
        posthog.startSessionRecording();
    }

    /** Graba la sesión aunque no caiga en el muestreo ni en los triggers configurados en PostHog. */
    forceSessionRecording() {
        if (readSupportSession()) return;
        posthog.startSessionRecording({ sampling: true, linked_flag: true, url_trigger: true, event_trigger: true });
    }

    /**
     * Check if a feature flag is enabled.
     */
    getExperimentVariant(flagKey: string): boolean {
        const enabled = posthog.isFeatureEnabled(flagKey) ?? false;
        if (flagKey === BANK_APIS_FLAG && enabled !== this.lastBankApisLogged) {
            this.lastBankApisLogged = enabled;
            this.logBankApisFlag('getExperimentVariant', { enabled });
        }
        return enabled;
    }

    /**
     * Reload feature flags from PostHog (call after login/logout)
     */
    reloadFeatureFlags(): Promise<void> {
        this.logBankApisFlag('reloadFeatureFlags:start');
        return new Promise(resolve => {
            let resolved = false;
            const done = (reason: string) => {
                if (!resolved) {
                    resolved = true;
                    this.logBankApisFlag('reloadFeatureFlags:done', { reason });
                    resolve();
                }
            };

            // Add callback to be notified when flags load
            this.flagsLoadedCallbacks.push(() => done('onFeatureFlags'));
            posthog.reloadFeatureFlags();
            setTimeout(() => done('timeout'), FLAG_TIMEOUT_MS);
        });
    }

    /** Temporary QA diagnostics for FINA-3138. Filter console by `[FINA-3138 bank-apis]`. */
    logBankApisFlag(stage: string, extra: Record<string, unknown> = {}): void {
        if (!BANK_APIS_FLAG_DEBUG) {
            return;
        }
        console.info(BANK_APIS_LOG, stage, { ...this.bankApisDebugSnapshot(), ...extra });
    }

    private bankApisDebugSnapshot(): Record<string, unknown> {
        try {
            const raw = posthog.isFeatureEnabled(BANK_APIS_FLAG);
            const featureFlags = posthog.featureFlags as {
                getFlags?: () => string[];
                getFlagVariants?: () => Record<string, string | boolean>;
            };
            const posthogAny = posthog as unknown as {
                getGroups?: () => unknown;
                get_property?: (key: string) => unknown;
            };
            return {
                enabled: raw ?? false,
                raw: raw ?? null,
                flags: featureFlags.getFlags?.() ?? [],
                variants: featureFlags.getFlagVariants?.() ?? null,
                distinctId: posthog.get_distinct_id?.() ?? null,
                groups: posthogAny.getGroups?.() ?? posthogAny.get_property?.('$groups') ?? null,
                href: typeof location !== 'undefined' ? location.href : undefined,
                host: environment.POSTHOG_HOST,
                keyPrefix: String(environment.POSTHOG_KEY || '').slice(0, 16),
            };
        } catch (err) {
            return { snapshotError: String(err) };
        }
    }
}
